<template>
  <div class="media-page page-ind-contactos h-full w-full p-4">

    <!-- <section class="media-hero mb-5">
      <h2 class="text-xl font-bold mb-2">
        Contactos — Llamadas y Minutas
        <span v-if="sectionLoading" class="kpi-loading-dot" title="Cargando..."></span>
      </h2>
      <p class="hero-subtitle">Seguimiento mensual de contacto con canales.</p>
    </section> -->

    <!-- Toolbar: mes izquierda, botones Registrar derecha -->
    <section class="ind-toolbar mb-4">
      <div>
        <label class="field-label block mb-1">Mes</label>
        <input v-model="filtroMes" type="month" class="ind-month-input" :max="mesMax" @change="cargarLista" />
      </div>
      <div class="registrar-group">
        <span class="registrar-label">Registrar</span>
        <div class="flex gap-2">
          <Button
            label="Llamada"
            icon="pi pi-phone"
            class="p-button-sm btn-reg-llamada"
            @click="abrirDesdeToolbar('llamada')"
          />
          <Button
            label="Minuta"
            icon="pi pi-file-edit"
            class="p-button-sm btn-reg-minuta"
            @click="abrirDesdeToolbar('minuta')"
          />
        </div>
      </div>
    </section>

    <!-- Skeleton inicial (carga en frío) -->
    <template v-if="cargando && !canalesResumen.length">
      <div class="kpi-skel-page">
        <section class="kpi-section mb-5">
          <h3 class="kpi-section-title">
            <i class="pi pi-phone" style="font-size:0.92rem;color:#3b82f6;opacity:0.85"></i>
            Llamadas y Minutas
          </h3>
          <KpiSectionSkeleton variant="table" :item-count="4" :col-count="4" />
        </section>
        <div class="text-center mt-2 mb-4">
          <span class="kpi-skel-loading-label">
            <i class="pi pi-spin pi-spinner mr-1.5" style="font-size:0.7rem"></i>
            Cargando contactos…
          </span>
        </div>
      </div>
    </template>

    <!-- Tabla resumen: una fila por canal -->
    <template v-else>
      <div class="kpi-page-content" :class="{ 'contactos-container--loading': sectionLoading }">
        <section class="media-table-wrap" :class="{ 'kpi-section--loading': sectionLoading }">
          <DataTable
            :value="canalesResumen"
            responsiveLayout="scroll"
            class="p-datatable-sm media-table"
          >
            <template #empty>
              <div class="media-empty-state">
                <i class="pi pi-calendar media-empty-icon"></i>
                <p class="media-empty-title">Sin contactos este mes</p>
                <p class="media-empty-hint">Usa los botones Llamada o Minuta para registrar el primero.</p>
              </div>
            </template>

            <Column header="Canal / Cliente" style="min-width:200px">
              <template #body="{ data }">
                <div class="flex items-center gap-2.5">
                  <div
                    class="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center"
                    :style="logoBackgroundStyle(data)"
                  >
                    <img
                      v-if="getLogoUrl(data)"
                      :src="getLogoUrl(data)"
                      :alt="data.radio_name || ''"
                      class="max-w-full max-h-full object-contain"
                      draggable="false"
                    />
                    <i v-else class="pi pi-megaphone text-white" style="font-size:0.85rem;opacity:0.5"></i>
                  </div>
                  <div class="canal-cell min-w-0">
                    <span class="canal-cell-name truncate">{{ data.radio_name }}</span>
                    <span v-if="data.client_name !== data.radio_name" class="canal-cell-client truncate">{{ data.client_name }}</span>
                  </div>
                </div>
              </template>
            </Column>

            <Column header="Llamada" style="min-width:170px">
              <template #body="{ data }">
                <div v-if="data.llamada" class="contact-chip chip-llamada">
                  <i class="pi pi-check-circle chip-icon"></i>
                  <span>{{ formatFecha(data.llamada.occurred_at) }}</span>
                  <span v-if="data.llamadaCount > 1" class="chip-count">x{{ data.llamadaCount }}</span>
                </div>
                <Button
                  v-else
                  label="+ Llamada"
                  icon="pi pi-phone"
                  class="p-button-sm p-button-text btn-add-llamada"
                  @click="abrirDesdeTabla(data, 'llamada')"
                />
              </template>
            </Column>

            <Column header="Minuta" style="min-width:170px">
              <template #body="{ data }">
                <div v-if="data.minuta" class="contact-chip chip-minuta">
                  <i class="pi pi-check-circle chip-icon"></i>
                  <span>{{ formatFecha(data.minuta.occurred_at) }}</span>
                  <span v-if="data.minutaCount > 1" class="chip-count">x{{ data.minutaCount }}</span>
                </div>
                <Button
                  v-else
                  label="+ Minuta"
                  icon="pi pi-file-edit"
                  class="p-button-sm p-button-text btn-add-minuta"
                  @click="abrirDesdeTabla(data, 'minuta')"
                />
              </template>
            </Column>

            <Column header="" style="min-width:56px">
              <template #body="{ data }">
                <Button
                  icon="pi pi-eye"
                  class="p-button-sm p-button-rounded btn-detalle"
                  @click="abrirDetalles(data.radio_id, data.radio_name)"
                  v-tooltip.top="'Ver historial de contacto'"
                />
              </template>
            </Column>
          </DataTable>
        </section>
      </div>
    </template>

    <!-- Dialog de registro -->
    <Dialog
      v-model:visible="dialogVisible"
      :header="dialogHeader"
      :modal="true"
      :style="{ width: '420px' }"
      :closable="!guardando"
    >
      <div class="flex flex-col gap-4 pt-2">

        <!-- Modo toolbar: busca por nombre de canal, cliente se muestra solo -->
        <template v-if="formMode === 'toolbar'">
          <div class="flex flex-col gap-1">
            <label class="field-label">Canal</label>
            <Dropdown
              v-model="form.radio_id"
              :options="todosLosCanales"
              optionLabel="name"
              optionValue="id"
              placeholder="Busca por nombre de canal..."
              filter
              filterPlaceholder="Escribe el nombre..."
              class="w-full"
              @change="onCanalChange"
            />
            <span v-if="clienteSeleccionadoNombre" class="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5 pl-1">
              Cliente: {{ clienteSeleccionadoNombre }}
            </span>
          </div>
        </template>

        <div class="flex flex-col gap-1">
          <label class="field-label">Fecha y hora</label>
          <input
            v-model="form.occurred_at"
            type="datetime-local"
            :max="maxDatetime"
            class="ind-datetime-input"
          />
        </div>

        <div class="flex flex-col gap-1">
          <label class="field-label">Notas <span class="font-normal text-zinc-400">(opcional)</span></label>
          <Textarea
            v-model="form.notes"
            rows="3"
            placeholder="Resumen del contacto..."
            class="w-full text-sm"
            :maxlength="5000"
          />
        </div>
      </div>

      <template #footer>
        <Button label="Cancelar" class="p-button-text" :disabled="guardando" @click="dialogVisible = false" />
        <Button label="Guardar" :loading="guardando" @click="guardar" />
      </template>
    </Dialog>

    <!-- Dialog de detalles del canal -->
      <Dialog
        v-model:visible="detallesDialogVisible"
        class="detalles-dialog"
        :modal="true"
        :style="{ width: '860px' }"
        :closable="true"
        :dismissableMask="true"
        :showHeader="false"
      >
        <!-- Header personalizado con estética del sitio -->
        <div class="detalles-header">
          <div class="detalles-header-info">
            <h3 class="detalles-header-title">{{ detallesRadioName }}</h3>
            <span class="detalles-header-sub">
              {{ contactosDelCanal.length }} contacto{{ contactosDelCanal.length !== 1 ? 's' : '' }} registrado{{ contactosDelCanal.length !== 1 ? 's' : '' }}
            </span>
          </div>
          <div class="detalles-header-badges" v-if="contactosDelCanal.length">
            <span class="detalle-badge badge-llamada">
              <i class="pi pi-phone mr-1" style="font-size:0.65rem"></i>
              {{ contactosDelCanal.filter(r => r.contact_type === 'llamada').length }}
            </span>
            <span class="detalle-badge badge-minuta">
              <i class="pi pi-file-edit mr-1" style="font-size:0.65rem"></i>
              {{ contactosDelCanal.filter(r => r.contact_type === 'minuta').length }}
            </span>
          </div>
        </div>

        <div class="detalles-body">
          <DataTable
            :value="contactosDelCanal"
            responsiveLayout="scroll"
            class="p-datatable-sm media-table"
          >
            <template #empty>
              <div class="media-empty-state">
                <i class="pi pi-inbox media-empty-icon"></i>
                <p class="media-empty-title">Sin actividad</p>
                <p class="media-empty-hint">No hay llamadas ni minutas registradas para este canal en el mes seleccionado.</p>
              </div>
            </template>

            <Column header="Tipo" style="min-width:52px;max-width:56px">
              <template #body="{ data }">
                <span
                  class="detalle-tipo-icono"
                  :class="data.contact_type === 'llamada'
                    ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-300'
                    : 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/40 dark:text-emerald-300'"
                  :title="data.contact_type === 'llamada' ? 'Llamada' : 'Minuta'"
                >
                  {{ data.contact_type === 'llamada' ? '📞' : '📋' }}
                </span>
              </template>
            </Column>

            <Column header="Fecha" style="min-width:110px">
              <template #body="{ data }">
                <span class="text-xs font-medium tabular-nums">{{ formatFecha(data.occurred_at) }}</span>
              </template>
            </Column>

            <Column header="Notas" style="min-width:300px">
              <template #body="{ data }">
                <div
                  v-if="data.notes"
                  class="detalle-nota"
                  :title="data.notes"
                >
                  <span class="detalle-nota-texto">{{ truncar(data.notes, 120) }}</span>
                </div>
                <span v-else class="text-zinc-300 dark:text-zinc-600 text-xs italic">—</span>
              </template>
            </Column>

            <Column header="Registró" style="min-width:100px">
              <template #body="{ data }">
                <span class="text-xs font-medium">{{ data.recorded_by_name || '—' }}</span>
              </template>
            </Column>

            <Column header="" style="min-width:52px">
              <template #body="{ data }">
                <Button
                  v-if="esMesActual(data.occurred_at)"
                  icon="pi pi-pencil"
                  class="p-button-sm p-button-rounded btn-editar-contacto"
                  @click="editarContacto(data)"
                  v-tooltip.top="'Editar este contacto'"
                />
              </template>
            </Column>
          </DataTable>
        </div>

        <template #footer>
          <div class="detalles-footer">
            <Button
              label="Cerrar"
              icon="pi pi-times"
              class="p-button-sm p-button-text"
              @click="detallesDialogVisible = false"
            />
          </div>
        </template>
      </Dialog>

  <Toast group="ind-contactos" position="bottom-right" />
  </div>
</template>

<script>
import axiosInstance from '@/axiosInstance';
import KpiSectionSkeleton from '@/components/KpiSectionSkeleton.vue';
import { useGlobalFilter } from '@/stores/globalFilterStore';

const gf = useGlobalFilter();

export default {
  name: 'IndicadoresOpContactos',

  components: { KpiSectionSkeleton },

  data() {
    const hoy = new Date();
    const mesActual = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}`;
    return {
      filtroMes: mesActual,
      registros: [],
      cargando: false,
      todosLosCanales: [],
      clientes: [],
      asesorSeleccionado: null,
      sectionLoadingDelayed: false,
      sectionLoadingTimer: null,
      dialogVisible: false,
      guardando: false,
      editRecordId: null,
      detallesDialogVisible: false,
      detallesRadioId: null,
      detallesRadioName: '',

      formMode: 'toolbar',
      form: {
        client_id: null,
        radio_id: null,
        contact_type: 'llamada',
        occurred_at: null,
        notes: '',
      },
    };
  },

  computed: {
    canalesDelCliente() {
      if (!this.form.client_id) return [];
      return this.todosLosCanales.filter(r => r.client_id === this.form.client_id);
    },

    mesMax() {
      const now = new Date();
      return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    },

    sectionLoading() {
      return this.sectionLoadingDelayed && this.canalesResumen.length > 0;
    },

    maxDatetime() {
      const now = new Date();
      return `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}T${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;
    },

    clienteSeleccionadoNombre() {
      if (!this.form.radio_id) return null;
      const canal = this.todosLosCanales.find(c => c.id === this.form.radio_id);
      return canal ? canal.client_name : null;
    },

    canalesResumen() {
      const map = {};
      const base = this.asesorSeleccionado
        ? this.registros.filter(r => Number(r.operator_id) === Number(this.asesorSeleccionado))
        : this.registros;
      base.forEach(r => {
        if (!map[r.radio_id]) {
          map[r.radio_id] = {
            radio_id: r.radio_id,
            radio_name: r.radio_name,
            client_id: r.client_id,
            client_name: r.client_name,
            mobileimage: r.mobileimage,
            color: r.color,
            llamada: null,
            llamadaCount: 0,
            minuta: null,
            minutaCount: 0,
          };
        }
        const row = map[r.radio_id];

        if (r.contact_type === 'llamada') {
          row.llamadaCount++;
          if (!row.llamada) row.llamada = r;
        } else {
          row.minutaCount++;
          if (!row.minuta) row.minuta = r;
        }
      });
      return Object.values(map).sort((a, b) => a.radio_name.localeCompare(b.radio_name));
    },

    contactosDelCanal() {
      if (!this.detallesRadioId) return [];
      return this.registros.filter(r => r.radio_id === this.detallesRadioId);
    },

    dialogHeader() {
      const accion = this.editRecordId ? 'Editar' : 'Registrar';
      const tipo = this.form.contact_type === 'llamada' ? 'Llamada' : 'Minuta';
      if (this.formMode === 'tabla') {
        const canal = this.todosLosCanales.find(c => c.id === this.form.radio_id);
        return canal ? `${accion} ${tipo} — ${canal.name}` : `${accion} ${tipo}`;
      }
      return `${accion} ${tipo}`;
    },
  },

  mounted() {
    this.asesorSeleccionado = gf.get('globalAsesorId');
    this._unsubFilter = gf.onChange(() => {
      this.asesorSeleccionado = gf.get('globalAsesorId');
    });
    this.cargarCanales();
    this.cargarLista();
  },

  watch: {
    cargando(val) {
      if (val) {
        this.sectionLoadingTimer = setTimeout(() => {
          this.sectionLoadingDelayed = true;
        }, 150);
      } else {
        clearTimeout(this.sectionLoadingTimer);
        this.sectionLoadingTimer = null;
        this.sectionLoadingDelayed = false;
      }
    },
  },

  beforeUnmount() {
    clearTimeout(this.sectionLoadingTimer);
    if (this._unsubFilter) this._unsubFilter();
  },

  methods: {
    async cargarCanales() {
      try {
        const res = await axiosInstance.post('/api/canal_metas_list', {
          token: localStorage.getItem('token'),
        });
        if (res.status === 200 && !res.data.error) {
          this.todosLosCanales = res.data.radios;
          const map = {};
          res.data.radios.forEach(r => {
            if (!map[r.client_id]) map[r.client_id] = { id: r.client_id, name: r.client_name };
          });
          this.clientes = Object.values(map).sort((a, b) => a.name.localeCompare(b.name));
        }
      } catch {
        this.toast('error', 'Error', 'No se pudieron cargar los canales');
      }
    },

    async cargarLista() {
      // Clamp para evitar navegar a meses futuros
      if (this.filtroMes > this.mesMax) {
        this.filtroMes = this.mesMax;
      }
      this.cargando = true;
      try {
        const res = await axiosInstance.post('/api/client_contact_records_list', {
          token: localStorage.getItem('token'),
          month: this.filtroMes,
        });
        if (res.status === 200 && !res.data.error) {
          this.registros = res.data.items;
          // Reiniciar asesor si el seleccionado ya no tiene registros
          if (this.asesorSeleccionado && this.registros.length) {
            const existe = this.registros.some(r => Number(r.operator_id) === Number(this.asesorSeleccionado));
            if (!existe) {
              this.asesorSeleccionado = null;
              gf.clear('globalAsesorId');
              gf.clear('globalAsesorName');
            }
          }
        } else {
          this.toast('error', 'Error al cargar', res.data.message || 'Error desconocido');
        }
      } catch {
        this.toast('error', 'Error de red', 'No se pudo conectar al servidor');
      } finally {
        this.cargando = false;
      }
    },

    abrirDetalles(radioId, radioName) {
      this.detallesRadioId = radioId;
      this.detallesRadioName = radioName;
      this.detallesDialogVisible = true;
    },

    editarContacto(record) {
      this.editRecordId = record.id;
      this.formMode = 'tabla';
      this.form.client_id = record.client_id;
      this.form.radio_id = record.radio_id;
      this.form.contact_type = record.contact_type;
      this.form.occurred_at = record.occurred_at ? record.occurred_at.slice(0, 16).replace(' ', 'T') : this.maxDatetime;
      this.form.notes = record.notes || '';
      this.detallesDialogVisible = false;
      this.dialogVisible = true;
    },

    esMesActual(occurredAt) {
      if (!occurredAt) return false;
      const ahora = new Date();
      const mesActual = `${ahora.getFullYear()}-${String(ahora.getMonth() + 1).padStart(2, '0')}`;
      return occurredAt.slice(0, 7) === mesActual;
    },

    abrirDesdeToolbar(tipo) {
      this.editRecordId = null;
      this.formMode = 'toolbar';
      this.form.client_id = null;
      this.form.radio_id = null;
      this.form.contact_type = tipo;
      this.form.occurred_at = this.maxDatetime;
      this.form.notes = '';
      this.dialogVisible = true;
    },

    abrirDesdeTabla(canalData, tipo) {
      this.editRecordId = null;
      this.formMode = 'tabla';
      this.form.client_id = canalData.client_id;
      this.form.radio_id = canalData.radio_id;
      this.form.contact_type = tipo;
      this.form.occurred_at = this.maxDatetime;
      this.form.notes = '';
      this.dialogVisible = true;
    },

    onCanalChange() {
      const canal = this.todosLosCanales.find(c => c.id === this.form.radio_id);
      this.form.client_id = canal ? canal.client_id : null;
    },

    async guardar() {
      if (!this.form.radio_id) {
        this.toast('warn', 'Requerido', 'Selecciona un canal');
        return;
      }
      this.guardando = true;
      try {
        const occurredAt = this.form.occurred_at
          ? this.form.occurred_at.replace('T', ' ') + ':00'
          : null;

        const esEdicion = !!this.editRecordId;
        const endpoint = esEdicion
          ? '/api/client_contact_record_update'
          : '/api/client_contact_record_save';

        const payload = {
          token: localStorage.getItem('token'),
          occurred_at: occurredAt,
          notes: this.form.notes || null,
        };

        if (esEdicion) {
          payload.id = this.editRecordId;
        } else {
          payload.client_id = this.form.client_id;
          payload.radio_id = this.form.radio_id;
          payload.contact_type = this.form.contact_type;
        }

        const res = await axiosInstance.post(endpoint, payload);
        if (res.status === 200 && !res.data.error) {
          this.dialogVisible = false;
          this.editRecordId = null;
          const actionLabel = esEdicion ? 'Actualizado' : 'Registrado';
          this.toast('success', actionLabel, `${this.form.contact_type === 'llamada' ? 'Llamada' : 'Minuta'} guardada`);
          const mesRegistro = (occurredAt || new Date().toISOString()).slice(0, 7);
          if (mesRegistro === this.filtroMes) this.cargarLista();
        } else {
          this.toast('error', 'Error al guardar', res.data.message || 'Error desconocido');
        }
      } catch {
        this.toast('error', 'Error de red', 'No se pudo conectar al servidor');
      } finally {
        this.guardando = false;
      }
    },

    truncar(text, max) {
      if (!text) return '';
      return text.length > max ? text.slice(0, max) + '…' : text;
    },

    formatFecha(isoStr) {
      if (!isoStr) return '—';
      const d = new Date(isoStr);
      return d.toLocaleString('es-MX', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false });
    },

    getLogoUrl(data) {
      if (data.mobileimage) return 'https://vybroo.net/images' + data.mobileimage;
      return '';
    },

    logoBackgroundStyle(data) {
      if (!data || !data.color) return { backgroundColor: '#3f3f46' };
      const hex = String(data.color).replace(/^#/, '').toLowerCase();
      if (/^[0-9a-f]{3}$/.test(hex) || /^[0-9a-f]{6}$/.test(hex)) {
        return { backgroundColor: '#' + hex };
      }
      return { backgroundColor: '#3f3f46' };
    },

    toast(severity, summary, detail) {
      this.$toast.add({ severity, summary, detail, group: 'ind-contactos', life: 4000 });
    },
  },
};
</script>

<style>
@import "@/assets/media-module-base.css";

.page-ind-contactos {
  --media-hero-bg: linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%);
  --media-hero-border: rgba(110, 231, 183, 0.4);
  --media-hero-bg-dark: linear-gradient(135deg, rgba(20, 30, 25, 0.95) 0%, rgba(24, 24, 27, 0.98) 100%);
  --media-hero-border-dark: rgba(52, 211, 153, 0.2);
}

.ind-toolbar {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.ind-month-input {
  height: 42px;
  border-radius: 10px;
  border: 1px solid rgba(148, 163, 184, 0.55);
  background: rgba(255, 255, 255, 0.96);
  padding: 0 10px;
  font-size: 0.9rem;
  color: inherit;
  outline: none;
}

.dark .ind-month-input {
  border-color: rgba(113, 113, 122, 0.48);
  background: rgba(24, 24, 27, 0.75);
  color: #e4e4e7;
  color-scheme: dark;
}

.ind-datetime-input {
  height: 42px;
  width: 100%;
  border-radius: 10px;
  border: 1px solid rgba(148, 163, 184, 0.55);
  background: rgba(255, 255, 255, 0.96);
  padding: 0 10px;
  font-size: 0.9rem;
  color: inherit;
  outline: none;
}

.dark .ind-datetime-input {
  border-color: rgba(113, 113, 122, 0.48);
  background: rgba(24, 24, 27, 0.75);
  color: #e4e4e7;
  color-scheme: dark;
}

.registrar-group {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}

.registrar-label {
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #71717a;
}

.btn-reg-llamada.p-button { background: #3b82f6 !important; border-color: #3b82f6 !important; color: #fff !important; }
.btn-reg-llamada.p-button:hover { background: #2563eb !important; border-color: #2563eb !important; }
.btn-reg-minuta.p-button { background: #10b981 !important; border-color: #10b981 !important; color: #fff !important; }
.btn-reg-minuta.p-button:hover { background: #059669 !important; border-color: #059669 !important; }

.canal-cell { display: flex; flex-direction: column; gap: 1px; }
.canal-cell-name { font-weight: 600; font-size: 0.875rem; }
.canal-cell-client { font-size: 0.75rem; color: #71717a; }

.contact-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 9px;
  border-radius: 20px;
  font-size: 0.78rem;
  font-weight: 500;
}
.chip-llamada { background: rgba(59,130,246,0.1); color: #2563eb; border: 1px solid rgba(59,130,246,0.25); }
.dark .chip-llamada { background: rgba(59,130,246,0.15); color: #93c5fd; border-color: rgba(59,130,246,0.3); }
.chip-minuta { background: rgba(16,185,129,0.1); color: #059669; border: 1px solid rgba(16,185,129,0.25); }
.dark .chip-minuta { background: rgba(16,185,129,0.15); color: #6ee7b7; border-color: rgba(16,185,129,0.3); }
.chip-icon { font-size: 0.75rem; }
.chip-count { font-size: 0.7rem; opacity: 0.7; margin-left: 2px; }

.btn-add-llamada.p-button-text { color: #3b82f6 !important; }
.btn-add-llamada.p-button-text:hover { background: rgba(59,130,246,0.08) !important; }
.btn-add-minuta.p-button-text { color: #10b981 !important; }
.btn-add-minuta.p-button-text:hover { background: rgba(16,185,129,0.08) !important; }

/* Fallback: oculta header nativo del Dialog si showHeader no funciona */
.detalles-dialog .p-dialog-header { display: none; }

/* Botón detalle en tabla principal */
.btn-detalle.p-button {
  width: 32px;
  height: 32px;
  background: transparent;
  border: 1px solid rgba(148,163,184,0.2);
  color: #94a3b8;
  transition: all 0.15s ease;
}
.btn-detalle.p-button:hover {
  background: rgba(59,130,246,0.06);
  border-color: rgba(59,130,246,0.35);
  color: #3b82f6;
}
.dark .btn-detalle.p-button {
  background: transparent;
  border-color: rgba(82,82,91,0.4);
  color: #71717a;
}
.dark .btn-detalle.p-button:hover {
  background: rgba(59,130,246,0.12);
  border-color: rgba(96,165,250,0.4);
  color: #93c5fd;
}

/* Dialog de detalles */
.detalles-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px 0 24px;
  gap: 12px;
}
.detalles-header-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.detalles-header-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: #0f172a;
}
.dark .detalles-header-title {
  color: #e4e4e7;
}
.detalles-header-sub {
  font-size: 0.78rem;
  color: #64748b;
}
.dark .detalles-header-sub {
  color: #a1a1aa;
}
.detalles-header-badges {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
}
.detalle-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
}
.badge-llamada {
  background: rgba(59,130,246,0.1);
  color: #2563eb;
}
.dark .badge-llamada {
  background: rgba(59,130,246,0.2);
  color: #93c5fd;
}
.badge-minuta {
  background: rgba(16,185,129,0.1);
  color: #059669;
}
.dark .badge-minuta {
  background: rgba(16,185,129,0.2);
  color: #6ee7b7;
}
.detalles-body {
  padding: 14px 18px 0 18px;
}
.detalles-footer {
  display: flex;
  justify-content: flex-end;
}

/* Ícono de tipo en dialog de detalles - solo emoji, grande y centrado */
.detalle-tipo-icono {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 999px;
  font-size: 1.1rem;
  line-height: 1;
  cursor: default;
}

/* Notas en dialog de detalles - más amplio */
.detalle-nota {
  background: rgba(241,245,249,0.7);
  border: 1px solid rgba(148,163,184,0.25);
  border-radius: 8px;
  padding: 7px 10px;
  cursor: help;
  min-height: 32px;
}
.dark .detalle-nota {
  background: rgba(39,39,42,0.6);
  border-color: rgba(82,82,91,0.4);
}
.detalle-nota-texto {
  font-size: 0.8rem;
  line-height: 1.45;
  color: #334155;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.dark .detalle-nota-texto {
  color: #d4d4d8;
}

/* Botón editar en dialog de detalles */
.btn-editar-contacto.p-button {
  width: 30px;
  height: 30px;
  background: transparent;
  border: 1px solid rgba(148,163,184,0.3);
  color: #64748b;
  transition: all 0.15s ease;
}
.btn-editar-contacto.p-button:hover {
  background: rgba(59,130,246,0.08);
  border-color: #3b82f6;
  color: #2563eb;
}
.dark .btn-editar-contacto.p-button {
  border-color: rgba(82,82,91,0.5);
  color: #a1a1aa;
}
.dark .btn-editar-contacto.p-button:hover {
  background: rgba(59,130,246,0.15);
  border-color: #60a5fa;
  color: #93c5fd;
}

/* ── Shimmer overlay en secciones durante recarga ── */
.kpi-section--loading {
  position: relative;
}

.kpi-section--loading::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 12px;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.55) 50%,
    transparent 100%
  );
  background-size: 200% 100%;
  animation: kpi-loading-shimmer 1.8s ease-in-out infinite;
  z-index: 2;
  pointer-events: none;
}

.dark .kpi-section--loading::before {
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.08) 50%,
    transparent 100%
  );
  background-size: 200% 100%;
}

@keyframes kpi-loading-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* ── Loading dot animado en títulos ── */
.kpi-loading-dot {
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #6366f1;
  animation: kpi-loading-dot-pulse 1.4s ease-in-out infinite;
  vertical-align: middle;
  position: relative;
  top: -1px;
  margin-left: 6px;
}

.dark .kpi-loading-dot {
  background: #818cf8;
}

@keyframes kpi-loading-dot-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.35; transform: scale(0.6); }
}

/* ── Skeleton page (carga en frío) ── */
.kpi-skel-page {
  animation: kpi-skel-fade-in 0.4s ease-out;
}

@keyframes kpi-skel-fade-in {
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
}

.kpi-skel-loading-label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  color: #94a3b8;
  letter-spacing: 0.02em;
}

.dark .kpi-skel-loading-label {
  color: #52525b;
}

/* ── Fade-in suave del contenido ── */
.kpi-page-content {
  animation: kpi-content-fade-in 0.35s ease-out;
}

@keyframes kpi-content-fade-in {
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* ── Secciones estilo KPI ── */
.kpi-section {
  position: relative;
  background: rgba(255, 255, 255, 0.6);
  border: 1px solid rgba(148, 163, 184, 0.3);
  border-radius: 12px;
  padding: 16px 18px;
}

.dark .kpi-section {
  background: rgba(24, 24, 27, 0.5);
  border-color: rgba(82, 82, 91, 0.4);
}

.kpi-section-title {
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: #1e293b;
  margin-bottom: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.dark .kpi-section-title {
  color: #d4d4d8;
}

.media-table-wrap.position-relative {
  position: relative;
}
</style>
